<script>
import moment from "moment";

import Portal from "../../layouts/portal";
import Membresia from "@/components/portal-membresia";
import { clienteComputed } from "@/state/helpers";
import { cargarSesiones, TIPOS_SESION } from "@/services/portal";

export default {
  page: { title: "Sesiones" },
  components: { Portal, Membresia },
  data() {
    return {
      tipos: TIPOS_SESION,
      tipo: "todas",
      busqueda: "",
      sesiones: [],
      cargando: true,
      error: null,
    };
  },
  computed: {
    ...clienteComputed,
    sesionesFiltradas() {
      const q = this.busqueda.trim().toLowerCase();
      return this.sesiones
        .filter((s) => this.tipo === "todas" || s.tipo === this.tipo)
        .filter((s) => {
          if (!q) return true;
          return [s.titulo, s.autor, s.descripcion]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q));
        });
    },
  },
  methods: {
    fecha(sesion) {
      if (sesion.fecha) return sesion.fecha;
      if (sesion.timestamp && sesion.timestamp.toDate) {
        return moment(sesion.timestamp.toDate()).format("DD [de] MMMM [de] YYYY");
      }
      return "";
    },
    async cargar() {
      this.cargando = true;
      this.error = null;
      try {
        this.sesiones = await cargarSesiones();
      } catch (err) {
        console.error("[portal] error al cargar sesiones:", err);
        this.error = "No pudimos cargar las sesiones. Inténtalo de nuevo en un momento.";
      } finally {
        this.cargando = false;
      }
    },
  },
  mounted() {
    // Sin membresía no se piden los datos siquiera: no tiene caso traer el
    // catálogo para después taparlo.
    if (this.tieneMembresia) this.cargar();
    else this.cargando = false;
  },
  watch: {
    // La membresía se resuelve de forma asíncrona; si llega después del montaje
    // hay que cargar el contenido en ese momento.
    tieneMembresia(nuevo) {
      if (nuevo && !this.sesiones.length) this.cargar();
    },
  },
};
</script>

<template>
  <Portal>
    <h1 class="h3 mb-1">Sesiones</h1>
    <p class="text-muted">Contenido de la red, para ver cuando quieras.</p>

    <Membresia v-if="!tieneMembresia && !cargandoCliente" seccion="El catálogo de sesiones" />

    <template v-else>
      <div class="d-flex flex-wrap align-items-center mb-4">
        <b-form-input v-model="busqueda" type="search" placeholder="Buscar por título o autor…"
          class="portal-buscador mr-3 mb-2" />
        <b-button-group size="sm" class="mb-2">
          <b-button v-for="t in tipos" :key="t.valor" :variant="tipo === t.valor ? 'primary' : 'outline-primary'"
            @click="tipo = t.valor">{{ t.texto }}</b-button>
        </b-button-group>
      </div>

      <b-alert :show="!!error" variant="warning">{{ error }}</b-alert>

      <p v-if="cargando" class="text-muted">Cargando sesiones…</p>

      <p v-else-if="sesionesFiltradas.length === 0" class="text-muted">
        No hay sesiones que coincidan con tu búsqueda.
      </p>

      <b-row v-else>
        <b-col v-for="sesion in sesionesFiltradas" :key="sesion.id" md="6" xl="4" class="mb-4">
          <router-link :to="{ name: 'portal-sesion', params: { id: sesion.id } }" class="sesion-card">
            <div class="sesion-portada">
              <img v-if="sesion.img" :src="sesion.img" :alt="sesion.titulo" />
              <div v-else class="sesion-portada-vacia"><i class="mdi mdi-play-circle"></i></div>
            </div>
            <div class="sesion-cuerpo">
              <h3 class="sesion-titulo">{{ sesion.titulo }}</h3>
              <p class="sesion-autor mb-1">{{ sesion.autor }}</p>
              <p class="sesion-fecha mb-0">{{ fecha(sesion) }}</p>
            </div>
          </router-link>
        </b-col>
      </b-row>
    </template>
  </Portal>
</template>

<style scoped>
.portal-buscador {
  max-width: 320px;
}

.sesion-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  height: 100%;
  color: inherit;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.sesion-card:hover {
  text-decoration: none;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

/*
 * Todas las portadas ocupan exactamente la misma caja, sin importar el tamaño
 * del archivo original.
 *
 * Se usa el truco del `padding-top` porcentual en lugar de `aspect-ratio`
 * porque este último es CSS reciente: en un navegador que no lo soporta la
 * caja se queda sin altura y cada tarjeta acaba midiendo distinto, que es
 * justo lo que hay que evitar en una rejilla.
 *
 * El porcentaje es alto/ancho: 75% = 4:3. Para cambiar la forma de todas las
 * portadas basta con tocar este número (56.25% = 16:9, 100% = cuadrado).
 */
.sesion-portada {
  position: relative;
  width: 100%;
  padding-top: 75%;
  background: #ede9f5;
  overflow: hidden;
}

.sesion-portada img,
.sesion-portada-vacia {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.sesion-portada img {
  object-fit: cover;
  object-position: center;
}

.sesion-portada-vacia {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: #4b2e83;
}

.sesion-cuerpo {
  padding: 16px;
  flex: 1;
}

.sesion-titulo {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.sesion-autor {
  color: #4b2e83;
  font-size: 0.9rem;
}

.sesion-fecha {
  color: #74788d;
  font-size: 0.8rem;
}
</style>
